import React, { useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";

/**
 * NewFolderModal — admin creates a folder (optionally as a sub-folder).
 *
 * Props:
 *   projectId
 *   parentId    null/undefined = root; folder id = create sub-folder
 *   parentLabel optional label to show in modal header (e.g. "Site Photos")
 *   existing    number of siblings (for sort_order)
 *   onClose
 *   onCreated
 */
export default function NewFolderModal({
  projectId, parentId = null, parentLabel = "", existing = 0, onClose, onCreated,
}) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.post(`/projects/${projectId}/folders`, {
        name,
        sort_order: existing,
        parent_id: parentId || null,
      });
      onCreated();
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="card-surface w-full max-w-md p-8 fade-up" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="font-display text-xl text-white">
              {parentId ? "New sub-folder" : "New folder"}
            </h3>
            {parentId && parentLabel && (
              <div className="text-xs text-[color:var(--atech-text-muted)] mt-1" data-testid="new-folder-parent-label">
                Inside: <span className="text-[color:var(--atech-text-dim)]">{parentLabel}</span>
              </div>
            )}
          </div>
          <button onClick={onClose} className="text-[color:var(--atech-text-dim)] hover:text-white" data-testid="modal-close">✕</button>
        </div>
        <form onSubmit={submit} className="space-y-4" data-testid="new-folder-form">
          <input
            required
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={parentId ? "e.g. Snagging" : "e.g. Proposed drawings"}
            className="w-full bg-[color:var(--atech-surface-elevated)] border border-white/10 rounded-[2px] px-3 py-2.5 text-sm focus:outline-none focus:border-[color:var(--atech-copper)]"
            data-testid="folder-name-input"
          />
          {err && <div className="text-xs text-[#F87171]" data-testid="folder-error">{err}</div>}
          <div className="flex justify-end gap-3">
            <button type="button" onClick={onClose} className="text-sm text-[color:var(--atech-text-dim)]">Cancel</button>
            <button type="submit" disabled={busy} className="btn-copper text-sm" data-testid="create-folder-submit">
              {busy ? "Creating…" : (parentId ? "Create sub-folder" : "Create folder")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
